import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import axios from "axios";
import reportWebVitals from './reportWebVitals';
import { GoogleOAuthProvider } from '@react-oauth/google';
import environment from './environments/environment';
import {
  clearAuthAndRedirect,
  isAccessTokenExpiredMessage,
  isSessionInvalidMessage,
  refreshAuthSession,
} from './utils/authRefresh';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

// Suppress harmless ResizeObserver loop error (common with ReactFlow)
const resizeObserverErrHandler = (e) => {
  const msg = e?.message || e?.reason?.message || '';
  if (msg.includes('ResizeObserver loop')) {
    e.stopImmediatePropagation();
    e.preventDefault();
    return false;
  }
};
window.addEventListener('error', resizeObserverErrHandler);
window.addEventListener('unhandledrejection', resizeObserverErrHandler);

// Monkey-patch ResizeObserver to debounce callbacks and prevent the "loop limit exceeded" error entirely.
// This stops the error from even reaching the React Dev Overlay.
const OriginalResizeObserver = window.ResizeObserver;
window.ResizeObserver = function (callback) {
  let debounceTimer;
  const debouncedCallback = (entries, observer) => {
    if (debounceTimer) cancelAnimationFrame(debounceTimer);
    debounceTimer = requestAnimationFrame(() => {
      callback(entries, observer);
    });
  };
  return new OriginalResizeObserver(debouncedCallback);
};

axios.defaults.headers.common["ngrok-skip-browser-warning"] = "true";
axios.defaults.withCredentials = true;
axios.interceptors.request.use((config) => {
  const currentAuth = config.headers?.Authorization || config.headers?.authorization;
  if (currentAuth && (String(currentAuth).includes("undefined") || String(currentAuth).includes("null"))) {
    delete config.headers.Authorization;
    delete config.headers.authorization;
  }
  return config;
});
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const errorMessage = error.response?.data?.errorMessage || error.response?.data?.message || "";
    const originalRequest = error.config;

    if (
      isAccessTokenExpiredMessage(errorMessage) &&
      originalRequest &&
      !originalRequest._authRetry &&
      !String(originalRequest.url || "").includes("/auth/refresh")
    ) {
      originalRequest._authRetry = true;
      const refreshed = await refreshAuthSession();
      if (refreshed) {
        return axios(originalRequest);
      }
      clearAuthAndRedirect();
    } else if (isSessionInvalidMessage(errorMessage)) {
      clearAuthAndRedirect();
    }
    return Promise.reject(error);
  }
);
// Fetch global header
const originalFetch = window.fetch;
window.fetch = async (url, options = {}) => {
  const currentAuth = options.headers?.Authorization || options.headers?.authorization;
  const headers = {
    ...(options.headers || {}),
    "ngrok-skip-browser-warning": "true",
  };
  if (currentAuth && (String(currentAuth).includes("undefined") || String(currentAuth).includes("null"))) {
    delete headers.Authorization;
    delete headers.authorization;
  }

  const updatedOptions = {
    ...options,
    headers,
    credentials: options.credentials || "include",
  };

  const response = await originalFetch(url, updatedOptions);
  if (!response.ok) {
    const data = await response.clone().json().catch(() => null);
    const errorMessage = data?.errorMessage || data?.message || "";
    if (isAccessTokenExpiredMessage(errorMessage) && !String(url || "").includes("/auth/refresh")) {
      const refreshed = await refreshAuthSession();
      if (refreshed) {
        return originalFetch(url, updatedOptions);
      }
      clearAuthAndRedirect();
    } else if (isSessionInvalidMessage(errorMessage)) {
      clearAuthAndRedirect();
    }
  }
  return response;
};

const root = ReactDOM.createRoot(document.getElementById('root'));

const app = <App />;

root.render(
  environment.googleClientId ? (
    <GoogleOAuthProvider clientId={environment.googleClientId}>
      {app}
    </GoogleOAuthProvider>
  ) : (
    app
  ),
);

// Register service worker for PWA support
serviceWorkerRegistration.register();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
